import {
	DownloadPeriod,
	DownloadRecordType,
	getDateRangeFromSelection,
} from '@velocity-exchange/common';
import useLazySubAccounts from 'src/hooks/useLazySubAccounts';
import { useCallback, useMemo, useState } from 'react';
import useDriftAccountStore, {
	AccountData,
} from 'src/stores/useDriftAccountsStore';
import { QUOTE_SPOT_MARKET_INDEX } from '@velocity-exchange/sdk';
import { usePerpMarkets } from 'src/hooks/perpMarkets/usePerpMarkets';
import { useSpotMarkets } from 'src/hooks/spotMarkets/useSpotMarkets';
import useSelect from 'src/components/primitives/Select/hooks/useSelect';
import {
	filterExportHistoryPeriodsForType,
	getExportHistoryPeriods,
	getIsExportHistoryMarketType,
	getIsExportHistoryUserType,
	isMarketTradesTimeframeExceeded,
} from 'src/components/Modals/ExportHistoryModal/utils';
import {
	EXPORT_HISTORY_DAYS,
	EXPORT_HISTORY_MONTHS,
	EXPORT_HISTORY_TYPES,
} from 'src/components/Modals/ExportHistoryModal/consts';
import useExportHistoryModalSubmit from 'src/components/Modals/ExportHistoryModal/hooks/useExportHistoryModalSubmit';
import useModal from 'src/components/Modals/hooks/useModal';

interface Params {
	type: DownloadRecordType;
}

export default function useExportHistoryModal({ type }: Params) {
	const { hide } = useModal();
	const currentUserKey = useDriftAccountStore((s) => s.currentUserKey);
	const accounts = useLazySubAccounts();
	const deletedAccounts = useDriftAccountStore((s) => s.deletedAccounts);
	const allAccounts = useMemo(
		() => [
			...accounts,
			// This is atrocious but it's insufferable trying to work with `Partial` here.
			...((deletedAccounts as AccountData[]) ?? []),
		],
		[accounts, deletedAccounts]
	);

	const [exportType, setExportType] = useState<DownloadRecordType>(type);

	// Default to 'all' months for user downloads.
	const defaultSelectedMonth = getIsExportHistoryUserType(exportType) ? 0 : 1;
	// Default to 'all' days always.
	const defaultSelectedDay = 0;

	const [selectedPeriod, setSelectedPeriod] = useState('week');
	const [selectedMonth, setSelectedMonth] = useState(defaultSelectedMonth);
	const [selectedDay, setSelectedDay] = useState(defaultSelectedDay);
	const [selectedMarket, setSelectedMarket] = useState('');
	const [selectedAccounts, setSelectedAccounts] = useState(
		currentUserKey ? [currentUserKey] : []
	);

	const exportPeriodOptions = useMemo(
		() =>
			getExportHistoryPeriods().filter((period) =>
				filterExportHistoryPeriodsForType(period.value, exportType)
			),
		[exportType]
	);

	const accountOptions = useMemo(() => {
		return allAccounts.map((acct) => {
			return {
				label: acct.name,
				value: acct.userKey,
				isSelected: selectedAccounts.includes(acct.userKey),
			};
		});
	}, [allAccounts, selectedAccounts]);

	const accountSelectDisplayValue = useMemo(() => {
		if (!selectedAccounts.length) {
			return 'Select accounts';
		}

		if (selectedAccounts.length === 1) {
			return (
				accounts.find((acct) => acct.userKey === selectedAccounts[0])?.name ??
				'Unnamed Account'
			);
		}

		if (selectedAccounts.length > 1) {
			return `${selectedAccounts.length} accounts selected`;
		}
	}, [accounts, selectedAccounts]);

	const allAccountsSelected = selectedAccounts.length === accountOptions.length;

	const handleAllAccountsSelectedChange = useCallback(
		(allSelected: boolean) => {
			setSelectedAccounts(
				allSelected ? allAccounts.map((acct) => acct.userKey) : []
			);
		},
		[allAccounts]
	);

	// Keep the fromDate and toDate updated with user input.
	const { from: fromDateStr, to: toDateStr } = useMemo(() => {
		if (!selectedPeriod.includes('custom')) {
			return getDateRangeFromSelection(selectedPeriod as DownloadPeriod);
		}

		return getDateRangeFromSelection('custom', {
			day: selectedDay === 0 ? undefined : selectedDay,
			month: selectedMonth === 0 ? undefined : selectedMonth - 1,
			year: parseFloat(
				exportPeriodOptions.find((period) => period.value === selectedPeriod)
					?.label ?? ''
			),
		});
	}, [selectedPeriod, selectedDay, selectedMonth]);

	const exportTypeSelectUtils = useSelect({
		selectedValue: exportType,
		onSelectedValueChange: (type) => {
			// Reset download month and day when the download type changes.
			setSelectedMonth(defaultSelectedMonth);
			setSelectedDay(defaultSelectedDay);

			setExportType(type);
		},
		options: EXPORT_HISTORY_TYPES,
	});

	const exportPeriodSelectUtils = useSelect({
		selectedValue: selectedPeriod,
		onSelectedValueChange: setSelectedPeriod,
		options: exportPeriodOptions,
	});

	const exportMonthSelectUtils = useSelect({
		selectedValue: selectedMonth,
		onSelectedValueChange: (month) => {
			// Reset download day if month is 'all' as it's not applicable for 'all' months.
			if (month === 0) {
				setSelectedDay(0);
			}

			setSelectedMonth(month);
		},
		options: EXPORT_HISTORY_MONTHS,
	});

	const exportDaySelectUtils = useSelect({
		selectedValue: selectedDay,
		onSelectedValueChange: setSelectedDay,
		options: EXPORT_HISTORY_DAYS,
	});

	const currentPerpMarkets = usePerpMarkets();
	const perpAssetFilterOptions = useMemo(() => {
		return currentPerpMarkets.map((market) => ({
			ticker: market.symbol,
			displayLabel: market.symbol,
			marketIndex: market.marketIndex,
		}));
	}, [currentPerpMarkets]);

	const currentSpotMarkets = useSpotMarkets();
	const spotAssetFilterOptions = useMemo(() => {
		return currentSpotMarkets
			.filter((market) => market.marketIndex !== QUOTE_SPOT_MARKET_INDEX)
			.map((market) => ({
				mint: market.mint?.toBase58() ?? market.symbol,
				ticker: market.symbol,
				displayLabel: `${market.symbol}/USDC`,
				marketIndex: market.marketIndex,
			}));
	}, [currentSpotMarkets]);

	const assetFilterOptions = useMemo(() => {
		return [...perpAssetFilterOptions, ...spotAssetFilterOptions];
	}, [perpAssetFilterOptions, spotAssetFilterOptions]);

	const isUserType = getIsExportHistoryUserType(exportType);

	const isMarketTradesExceedsLimit =
		exportType === 'market-trades' &&
		isMarketTradesTimeframeExceeded(selectedPeriod, selectedMonth);

	const isSubmitDisabled = useMemo(() => {
		if (getIsExportHistoryMarketType(exportType) && !selectedMarket) {
			return true;
		}

		if (getIsExportHistoryUserType(exportType) && !selectedAccounts?.length) {
			return true;
		}

		// Market trades with custom year must have a specific month selected (31 days max)
		if (isMarketTradesExceedsLimit) {
			return true;
		}

		return false;
	}, [
		exportType,
		selectedMarket,
		selectedAccounts,
		isMarketTradesExceedsLimit,
	]);

	const { isSubmitting, handleSubmit } = useExportHistoryModalSubmit({
		exportType,
		from: fromDateStr,
		to: toDateStr,
		// If this isn't a user type record, only provide the current user account,
		// as providing multiple accounts will cause the API to reply with an error.
		userKeys: isUserType
			? selectedAccounts
			: currentUserKey
				? [currentUserKey]
				: [],
		accountOptions: allAccounts,
		marketSymbol: selectedMarket,
		isSubmitDisabled,
		hide,
	});

	return {
		exportType,
		selectedMarket,
		setSelectedMarket,
		selectedPeriod,
		selectedMonth,
		accountOptions,
		accountSelectDisplayValue,
		selectedAccounts,
		setSelectedAccounts,
		allAccountsSelected,
		handleAllAccountsSelectedChange,
		exportTypeSelectUtils,
		exportPeriodSelectUtils,
		exportMonthSelectUtils,
		exportDaySelectUtils,
		assetFilterOptions,
		isSubmitDisabled,
		isMarketTradesExceedsLimit,
		isSubmitting,
		handleSubmit,
		handleClose: hide,
	};
}
